import { createApi } from "@reduxjs/toolkit/query/react";

import { allPagesOf } from "../allPages";
import { baseQueryWithReauth } from "../authorization/authService";

// The feedback limits refuse with 429 and Retry-After, the seconds until the sender
// may submit again; the forms say when to try again (utils/feedbackLimit.js, #119).
// The browser shows the header to the page when the API is on the page's own origin,
// as on the portal; a cross-origin API would have to expose it (CORS).
const withRetryAfter = (error, meta) => ({
  ...error,
  retryAfter: meta?.response?.headers?.get("Retry-After") ?? null,
});

export const feedbackApi = createApi({
  reducerPath: "feedbackApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Feedback"],
  endpoints: (builder) => ({
    createFeedback: builder.mutation({
      query: (feedbackData) => ({
        url: "/feedback",
        method: "POST",
        body: feedbackData,
      }),
      transformErrorResponse: withRetryAfter,
      invalidatesTags: ["Feedback"],
    }),
    submitUnregisteredUserFeedback: builder.mutation({
      query: (formData) => ({
        url: "/feedback/submit-unregistered-user",
        method: "POST",
        body: formData,
      }),
      transformErrorResponse: withRetryAfter,
      invalidatesTags: ["Feedback"],
    }),
    updateFeedbackResolvedStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/feedback/${id}/resolve`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["Feedback"],
    }),
    // Every page of the issues: the page draws its table, filters and counts from them (#136).
    getFeedbackList: builder.query({
      queryFn: allPagesOf((params) => ({ url: "/feedback/list", params })),
      providesTags: ["Feedback"],
    }),
    getFeedbackDetail: builder.query({
      query: (id) => ({
        url: `/feedback/${id}`,
        method: "GET",
      }),
      providesTags: ["Feedback"],
    }),
  }),
});

export const {
  useCreateFeedbackMutation,
  useSubmitUnregisteredUserFeedbackMutation,
  useUpdateFeedbackResolvedStatusMutation,
  useGetFeedbackListQuery,
  useGetFeedbackDetailQuery,
} = feedbackApi;
